import React, { useState, useRef, useCallback } from "react";
import { mediaSrc } from "../api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { X, ZoomIn, ZoomOut, Link2, Link2Off, RotateCcw } from "lucide-react";

function Pane({ media, transform, setTransform, sync, label }) {
  const ref = useRef(null);
  const dragRef = useRef(null);

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 1.15 : 0.87;
    setTransform((tr) => ({ ...tr, scale: Math.min(8, Math.max(0.5, tr.scale * delta)) }));
  };

  const onPointerDown = (e) => {
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = { x: cx, y: cy };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    e.preventDefault();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    const dx = cx - dragRef.current.x;
    const dy = cy - dragRef.current.y;
    dragRef.current = { x: cx, y: cy };
    setTransform((tr) => ({ ...tr, x: tr.x + dx, y: tr.y + dy }));
  };
  const onPointerUp = () => { dragRef.current = null; };

  return (
    <div
      ref={ref}
      className="flex-1 overflow-hidden relative bg-black/40 rounded-lg cursor-grab active:cursor-grabbing"
      onWheel={onWheel}
      onMouseDown={onPointerDown}
      onMouseMove={onPointerMove}
      onMouseUp={onPointerUp}
      onMouseLeave={onPointerUp}
      onTouchStart={onPointerDown}
      onTouchMove={onPointerMove}
      onTouchEnd={onPointerUp}
    >
      <Badge className="absolute top-2 left-2 z-10 bg-black/60 text-white text-[10px]">{label}</Badge>
      <div className="w-full h-full flex items-center justify-center">
        <img
          src={mediaSrc(media)}
          alt=""
          draggable={false}
          className="max-w-full max-h-full object-contain no-select"
          style={{ transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`, transition: dragRef.current ? "none" : "transform 0.05s linear" }}
        />
      </div>
    </div>
  );
}

export function CompareView({ open, left, right, onClose }) {
  const { t } = useApp();
  const [sync, setSync] = useState(true);
  const [sharedT, setSharedT] = useState({ scale: 1, x: 0, y: 0 });
  const [leftT, setLeftT] = useState({ scale: 1, x: 0, y: 0 });
  const [rightT, setRightT] = useState({ scale: 1, x: 0, y: 0 });

  const reset = useCallback(() => {
    setSharedT({ scale: 1, x: 0, y: 0 });
    setLeftT({ scale: 1, x: 0, y: 0 });
    setRightT({ scale: 1, x: 0, y: 0 });
  }, []);

  if (!open || !left || !right) return null;

  const zoom = (dir) => {
    const f = dir > 0 ? 1.25 : 0.8;
    if (sync) setSharedT((tr) => ({ ...tr, scale: Math.min(8, Math.max(0.5, tr.scale * f)) }));
    else {
      setLeftT((tr) => ({ ...tr, scale: Math.min(8, Math.max(0.5, tr.scale * f)) }));
      setRightT((tr) => ({ ...tr, scale: Math.min(8, Math.max(0.5, tr.scale * f)) }));
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col pt-safe" data-testid="photo-compare-view">
      <div className="flex items-center justify-between px-3 py-2">
        <Button variant="ghost" size="icon" data-testid="compare-close" onClick={onClose} className="text-white hover:bg-white/10 h-11 w-11">
          <X className="h-5 w-5" />
        </Button>
        <span className="text-white/80 text-sm font-medium">{t("compare")}</span>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => zoom(1)} data-testid="compare-zoom-in" className="text-white hover:bg-white/10 h-11 w-11">
            <ZoomIn className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => zoom(-1)} data-testid="compare-zoom-out" className="text-white hover:bg-white/10 h-11 w-11">
            <ZoomOut className="h-5 w-5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={reset} data-testid="compare-reset" className="text-white hover:bg-white/10 h-11 w-11">
            <RotateCcw className="h-5 w-5" />
          </Button>
          <Button
            variant="ghost"
            data-testid="photo-compare-sync-toggle"
            onClick={() => setSync((s) => !s)}
            className={`h-11 px-3 ${sync ? "text-accent" : "text-white/60"} hover:bg-white/10`}
          >
            {sync ? <Link2 className="h-5 w-5 mr-1.5" /> : <Link2Off className="h-5 w-5 mr-1.5" />}
            <span className="text-xs hidden sm:inline">{t("sync_zoom")}</span>
          </Button>
        </div>
      </div>
      <div className="flex-1 flex flex-col sm:flex-row gap-2 p-2 min-h-0">
        <div className="flex-1 flex min-h-0" data-testid="photo-compare-left">
          <Pane media={left} transform={sync ? sharedT : leftT} setTransform={sync ? setSharedT : setLeftT} sync={sync} label={left.version_label || left.category || "A"} />
        </div>
        <div className="flex-1 flex min-h-0" data-testid="photo-compare-right">
          <Pane media={right} transform={sync ? sharedT : rightT} setTransform={sync ? setSharedT : setRightT} sync={sync} label={right.version_label || right.category || "B"} />
        </div>
      </div>
    </div>
  );
}
